import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { saveBooking } from '../data/bookingStorage';
import StepIndicator from '../components/StepIndicator';
import { formatDate } from '../data/dateLocale';
import { CreditCard, Lock, ChevronDown, Check } from 'lucide-react';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { selectedTrip, selectedReturnTrip, searchParams, passengerInfo, setCurrentStep, totalPrice, seatAssignments } = useBooking();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const bookingSaved = useRef(false);

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardName, setCardName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(720);
  const [selectedBank, setSelectedBank] = useState(null);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');

  useEffect(() => { setCurrentStep(4); }, [setCurrentStep]);
  useEffect(() => {
    if (timer <= 0 || success) return;
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer, success]);

  useEffect(() => {
    if (!selectedTrip) navigate('/');
  }, [selectedTrip, navigate]);

  if (!selectedTrip) return null;

  const formatCardNumber = (val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 16);
    return cleaned.replace(/(.{4})/g, '$1 ').trim();
  };
  const formatExpiry = (val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 4);
    if (cleaned.length >= 3) return cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    return cleaned;
  };
  const doSaveBooking = () => {
    if (bookingSaved.current) return;
    bookingSaved.current = true;
    saveBooking({
      userEmail: user?.email || passengerInfo.email,
      trip: selectedTrip,
      returnTrip: selectedReturnTrip,
      departureDate: searchParams.departureDate,
      returnDate: searchParams.returnDate,
      passengers: searchParams.passengers,
      passengerInfo,
      seatAssignments,
      totalPrice: totalPrice(),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2000);
  };

  const handleBankPayment = (bank) => {
    setSelectedBank(bank);
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2500);
  };

  const handleBusinessLogin = () => {
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); doSaveBooking(); }, 2000);
  };

  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  if (success) {
    return (
      <div className="min-h-screen bg-[#f0f0f1] flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 md:p-12 max-w-md w-full mx-4 text-center shadow-lg">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#2e9e5a] flex items-center justify-center">
            <Check size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-3">{t('success.title')}</h1>
          <p className="text-sm text-[#555] mb-6">
            {t('success.ticketSent')} <strong>{passengerInfo.email}</strong>
          </p>
          <div className="bg-[#f0f0f1] rounded-lg p-4 mb-6 text-left">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#555]">{t('success.route')}</span>
              <span className="font-medium">{selectedTrip.from} → {selectedTrip.to}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#555]">{t('success.date')}</span>
              <span className="font-medium">{formatDate(searchParams.departureDate, lang)}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#555]">{t('success.time')}</span>
              <span className="font-medium">{selectedTrip.departureTime} — {selectedTrip.arrivalTime}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#555]">{t('success.passenger')}</span>
              <span className="font-medium">{passengerInfo.firstName} {passengerInfo.lastName}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-2 border-t mt-2">
              <span>{t('success.totalLabel')}</span>
              <span>{totalPrice()} €</span>
            </div>
          </div>
          <div className="space-y-2">
            {user && (
              <button onClick={() => navigate('/account')} className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm px-8 py-3 rounded-lg transition-colors w-full uppercase tracking-wider">
                {t('account.myTrips')}
              </button>
            )}
            <button onClick={() => navigate('/')} className={`${user ? 'border border-gray-300 text-[#484c55] hover:bg-gray-50' : 'bg-[#c30909] hover:bg-[#a00707] text-white'} font-semibold text-sm px-8 py-3 rounded-lg transition-colors w-full uppercase tracking-wider`}>
              {t('success.backHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <StepIndicator />
      <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1">
            <h1 className="text-xl font-bold mb-6">{t('payment.title')}</h1>

            <div className="bg-white rounded-xl p-4 shadow-sm mb-4 flex items-center justify-end gap-2">
              <div className="flex items-center gap-1">
                <div className="bg-[#c30909] text-white text-sm font-bold w-8 h-8 flex items-center justify-center rounded">{String(minutes).padStart(2, '0')}</div>
                <div className="bg-[#c30909] text-white text-sm font-bold w-8 h-8 flex items-center justify-center rounded">{String(seconds).padStart(2, '0')}</div>
              </div>
              <span className="text-[10px] text-[#777] uppercase">{t('payment.minutes')} {t('payment.seconds')}</span>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="flex border-b">
                <div className="flex-1 py-3 text-sm font-medium text-[#484c55] text-center relative">
                  {t('payment.card')}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#c30909]" />
                </div>
              </div>

              <div className="p-6">
                {true ? (
                  <form onSubmit={handleSubmit}>
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-5 h-5 rounded-full border-2 border-[#c30909] flex items-center justify-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#c30909]" />
                      </div>
                      <CreditCard size={20} className="text-[#555]" />
                      <span className="text-sm font-medium">{t('payment.cardLabel')}</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cardNumber')}</label>
                        <div className="relative">
                          <input type="text" required value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors pr-24" />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            <div className="w-8 h-5 bg-red-600 rounded text-[6px] text-white flex items-center justify-center font-bold">MC</div>
                            <div className="w-8 h-5 bg-blue-800 rounded text-[6px] text-white flex items-center justify-center font-bold">VISA</div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.expiry')}</label>
                          <input type="text" required value={expiry} onChange={(e) => setExpiry(formatExpiry(e.target.value))} placeholder="MM/YY" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cvv')}</label>
                          <div className="relative">
                            <input type="text" required value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))} placeholder={t('payment.cvvPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors pr-10" />
                            <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#484c55]/30" />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#555] mb-1.5">{t('payment.cardName')}</label>
                        <input type="text" required value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder={t('payment.cardNamePlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909] transition-colors" />
                      </div>
                      <button type="submit" disabled={processing} className="w-full bg-[#c30909] hover:bg-[#a00707] disabled:bg-[#c30909]/50 text-white font-semibold text-sm uppercase tracking-wider py-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                        {processing ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('payment.processing')}</>
                        ) : (
                          <><Lock size={14} />{t('payment.pay')} {totalPrice()} €</>
                        )}
                      </button>
                    </div>
                  </form>
                ) : null}
                <p className="text-[10px] text-[#484c55]/40 mt-4">ⓘ {t('payment.terms')}</p>
              </div>
            </div>
          </div>

          <div className="w-full md:w-[340px] flex-shrink-0">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-4">
              <h2 className="text-lg font-bold mb-4">{t('summary.title')}</h2>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#c30909]" />
                  <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.outbound')}</span>
                  <ChevronDown size={14} className="text-[#484c55]/30 ml-auto" />
                </div>
                <p className="text-sm font-bold">{formatDate(searchParams.departureDate, lang)}</p>
                <p className="text-xs text-[#555] mt-1">{selectedTrip.departureTime} {selectedTrip.from} — {selectedTrip.arrivalTime} {selectedTrip.to}</p>
              </div>
              {selectedReturnTrip && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#484c55]/30" />
                    <span className="text-xs font-semibold text-[#777] uppercase">{t('summary.return')}</span>
                    <ChevronDown size={14} className="text-[#484c55]/30 ml-auto" />
                  </div>
                  <p className="text-sm font-bold">{formatDate(searchParams.returnDate, lang)}</p>
                  <p className="text-xs text-[#555] mt-1">{selectedReturnTrip.departureTime} {selectedReturnTrip.from} — {selectedReturnTrip.arrivalTime} {selectedReturnTrip.to}</p>
                </div>
              )}
              <div className="border-t pt-4 mt-4">
                <button
                  onClick={() => setVoucherOpen(!voucherOpen)}
                  className="text-[#c30909] text-xs font-semibold uppercase tracking-wider mb-3 hover:underline"
                >
                  {t('payment.addVoucher')}
                </button>
                {voucherOpen && (
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value)}
                      placeholder={t('search.enterCode')}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#c30909]"
                    />
                    <button
                      onClick={() => { setVoucherCode(''); setVoucherOpen(false); }}
                      className="bg-[#c30909] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#a00707] transition-colors"
                    >
                      OK
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#555]">{t('summary.passenger')} × {Object.values(searchParams.passengers).reduce((a, b) => a + b, 0)}</span>
                  <span className="text-sm font-medium">{totalPrice()} €</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-base font-bold">{t('summary.total')}</span>
                  <span className="text-xl font-bold">{totalPrice()} €</span>
                </div>
              </div>
              <p className="text-[10px] text-[#484c55]/40 mt-4 leading-relaxed">ⓘ {t('payment.corpDiscount')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
